import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { partnerAPI } from '../services/api';

const AuthContext = createContext(null);

/**
 * Read a cookie by name.
 * Auth portal sets tokens as cookies on .elebyte.io domain.
 */
function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    return decodeURIComponent(parts.pop().split(';').shift());
  }
  return null;
}

function deleteCookie(name) {
  // Extract the root domain from the current hostname
  const hostname = window.location.hostname;
  const parts = hostname.split('.');

  // For domains like subdomain.example.com, we want .example.com
  // For localhost or IP addresses, we don't set a domain
  let cookieDomain = '';
  if (parts.length >= 2 && !hostname.match(/^\d+\.\d+\.\d+\.\d+$/)) {
    // Not an IP address and has at least 2 parts
    cookieDomain = `.${parts.slice(-2).join('.')}`;

    // Clear cookie with the calculated domain
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${cookieDomain}`;
  }

  // Also try to clear without domain specification (for localhost/different domains)
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/`;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Auth portal stores tokens in cookies on .elebyte.io domain after login,
    // then redirects back here. Read from cookies (primary) or localStorage (fallback).
    const token =
      getCookie('elebyte_id_token') ||
      getCookie('elebyte_access_token') ||
      localStorage.getItem('partner_access_token');

    const refreshToken =
      getCookie('elebyte_refresh_token') ||
      localStorage.getItem('partner_refresh_token');

    if (token) {
      // Also persist to localStorage so we have it if cookies are cleared
      localStorage.setItem('partner_access_token', token);
      if (refreshToken) {
        localStorage.setItem('partner_refresh_token', refreshToken);
      }

      // Decode JWT to get user info
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        // Check expiry
        if (payload.exp && payload.exp * 1000 < Date.now()) {
          performLogout();
        } else {
          // Validate role claim - only allow 'partner' role
          const userRole = payload['custom:role'] || payload.role || '';
          if (userRole !== 'partner') {
            console.error('Access denied: User role is not "partner"', { role: userRole });
            performLogout();
          } else {
            setUser({
              email: payload.email || payload['custom:email'] || '',
              role: userRole,
              sub: payload.sub || '',
            });
          }
        }
      } catch {
        performLogout();
      }
    }

    setLoading(false);
  }, []);

  function performLogout() {
    localStorage.removeItem('partner_access_token');
    localStorage.removeItem('partner_refresh_token');
    deleteCookie('elebyte_access_token');
    deleteCookie('elebyte_id_token');
    deleteCookie('elebyte_refresh_token');
    setUser(null);
  }

  const login = useCallback(() => {
    const redirectUrl = encodeURIComponent(window.location.origin);
    window.location.href = `https://auth.elebyte.io/login?redirect=${redirectUrl}&role=partner`;
  }, []);

  const logout = useCallback(async () => {
    try {
      // Call backend logout endpoint to invalidate tokens
      await partnerAPI.logout();
    } catch (error) {
      // Even if the backend call fails, we should still clear local tokens
      console.error('Failed to call logout endpoint:', error);
    } finally {
      // Always clear local tokens
      performLogout();
    }
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
